import dayjs, { type Dayjs } from 'dayjs';
import { type ReactElement, useCallback, useMemo, useState } from 'react';
import {
  buildSuiteCounts,
  type SuiteStatusKey,
} from '@/features/dashboard/components/dashboardStatusDataHelpers';
import { formatRangeWindowNoDataText } from '@/features/dashboard/hooks';
import { querySuiteExecutions, querySuiteExecutionStatusSummary } from '@/features/executions/data';
import { CLONE_TABLE_PANEL_HEADER_HEIGHT } from '@/features/shared/components/cloneTable/cloneTableConstants';
import { EntityTrendsPageShell } from '@/features/shared/components/EntityTrendsPageShell';
import { useCloneDateWindow } from '@/features/shared/hooks/useCloneDateWindow';
import { useEntityTrendsPage } from '@/features/shared/hooks/useEntityTrendsPage';
import { resolveEntityTags } from '@/features/shared/tags';
import { SuiteExecutionsTableClone } from '@/features/suites/components/SuiteExecutionsTableClone';
import { SuitesTableClone } from '@/features/suites/components/SuitesTableClone';
import { querySuites, querySuiteSummary } from '@/features/suites/data/suites';
import { SuiteExecutionsCloneToolbar } from '@/features/suites/pages/SuiteExecutionsCloneToolbar';
import {
  cloneExecutionDefaultColumnVisibilityModel,
  cloneExecutionDefaultSortDirection,
  cloneExecutionDefaultSortKey,
  cloneExecutionSortFieldMap,
  type CloneExecutionSortDirection,
  type CloneExecutionSortKey,
} from '@/features/suites/pages/suiteExecutionsPageCloneConfig';
import {
  SuitesCloneToolbar,
  type CloneDateQuickFilter,
} from '@/features/suites/pages/SuitesCloneToolbar';
import {
  cloneDefaultColumnVisibilityModel,
  cloneDefaultSortDirection,
  cloneDefaultSortKey,
  cloneSortFieldMap,
  type CloneSortDirection,
  type CloneSortKey,
} from '@/features/suites/pages/suitesPageCloneConfig';
import { suiteExecutionSummaryStatusOptions } from '@/shared/constants/statusOptions';
import { gridPaths, queryKeys, useResolvedFeatureFlags } from '@/shared/hooks';
import { Stack } from '@/ui';
import {
  buildDateRangeFilters,
  buildEqFilterValue,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import { Panel } from '@/ui/composites/Panel';
import { Box, Tab, Tabs, type GridColumnVisibilityModel } from '@/ui/primitives';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type {
  QueryResult,
  SuiteExecutionDto,
  SuiteExecutionStatus,
  SuiteExecutionStatusSummaryDto,
  SuiteListItemDto,
  SuiteSummaryDto,
} from '@flowqa/shared';

type CloneSuitesPanelTab = 'suites' | 'executions';

export const SuitesPage = (): ReactElement => {
  const { flags: featureFlags } = useResolvedFeatureFlags();
  const cloneTableEnabled = featureFlags?.suitesCloneTable ?? false;
  const viewModel = useEntityTrendsPage<
    SuiteListItemDto,
    SuiteSummaryDto | SuiteExecutionStatusSummaryDto
  >({
    loadingMessage: 'Loading suites...',
    metricsScope: 'suite',
    gridPath: gridPaths.suites,
    executionGridPath: gridPaths.suiteExecutions,
    entitySearchField: 'name',
    executionSearchField: 'suiteName',
    statusOptions: suiteExecutionSummaryStatusOptions.map((option) => ({
      value: option.value as Exclude<SuiteStatusKey, 'untested'>,
      label: option.label,
    })),
    summaryKey: (projectId, organisationId, filtersKey) =>
      queryKeys.suiteSummary(projectId, organisationId, filtersKey),
    executionSummaryKey: (projectId, organisationId, filtersKey) =>
      queryKeys.suiteExecutionSummary(projectId, organisationId, filtersKey),
    summaryPrefixKey: queryKeys.suiteSummaryPrefix,
    executionSummaryPrefixKey: queryKeys.suiteExecutionSummaryPrefix,
    queryLatestSummary: querySuiteSummary,
    queryExecutionSummary: querySuiteExecutionStatusSummary,
    buildCounts: buildSuiteCounts,
    selectThresholds: (thresholds) => thresholds.suites,
    queryEntityTable: async (
      input: GridQueryInput,
      {
        selectedProjectId,
        selectedOrganisationId,
        rangeStartIso,
        rangeEndIso,
        previousRangeStartIso,
        previousRangeEndIso,
        deltasEnabled,
      },
    ): Promise<QueryResult<SuiteListItemDto>> =>
      querySuites(selectedProjectId, selectedOrganisationId ?? undefined, {
        ...input,
        metricsStartDay: rangeStartIso ?? undefined,
        metricsEndDay: rangeEndIso ?? undefined,
        previousMetricsStartDay: previousRangeStartIso ?? undefined,
        previousMetricsEndDay: previousRangeEndIso ?? undefined,
        include: {
          project: true,
          latestExecution: true,
          latestTestResults: { counts: true },
          metrics: true,
          metricDeltas: deltasEnabled && previousRangeStartIso !== null,
        },
      }),
  });

  const [cloneDateQuickFilter, setCloneDateQuickFilter] = useState<CloneDateQuickFilter>('7d');
  const [cloneFromDate, setCloneFromDate] = useState<Dayjs | null>(null);
  const [cloneToDate, setCloneToDate] = useState<Dayjs | null>(null);
  const [debouncedCloneSearch, setDebouncedCloneSearch] = useState('');
  const [cloneColumnVisibilityModel, setCloneColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(cloneDefaultColumnVisibilityModel);
  const [cloneSortKey, setCloneSortKey] = useState<CloneSortKey>(cloneDefaultSortKey);
  const [cloneSortDirection, setCloneSortDirection] =
    useState<CloneSortDirection>(cloneDefaultSortDirection);
  const [cloneFilterStatuses, setCloneFilterStatuses] = useState<SuiteExecutionStatus[]>([]);
  const [cloneFilterTags, setCloneFilterTags] = useState<string[]>([]);
  const [cloneVisibleRows, setCloneVisibleRows] = useState<SuiteListItemDto[]>([]);
  const [cloneActiveTab, setCloneActiveTab] = useState<CloneSuitesPanelTab>('suites');
  const [debouncedCloneExecutionSearch, setDebouncedCloneExecutionSearch] = useState('');
  const [cloneExecutionColumnVisibilityModel, setCloneExecutionColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(cloneExecutionDefaultColumnVisibilityModel);
  const [cloneExecutionSortKey, setCloneExecutionSortKey] = useState<CloneExecutionSortKey>(
    cloneExecutionDefaultSortKey,
  );
  const [cloneExecutionSortDirection, setCloneExecutionSortDirection] =
    useState<CloneExecutionSortDirection>(cloneExecutionDefaultSortDirection);
  const [cloneExecutionFilterStatuses, setCloneExecutionFilterStatuses] = useState<
    SuiteExecutionStatus[]
  >([]);
  const [cloneExecutionFilterTags, setCloneExecutionFilterTags] = useState<string[]>([]);
  const [cloneVisibleExecutionRows, setCloneVisibleExecutionRows] = useState<SuiteExecutionDto[]>(
    [],
  );

  const cloneAvailableTags = useMemo(() => {
    const tags = new Set<string>();
    cloneVisibleRows.forEach((row) => {
      resolveEntityTags(row).forEach((tag) => {
        tags.add(tag);
      });
    });
    return Array.from(tags.values()).sort((left, right) => left.localeCompare(right));
  }, [cloneVisibleRows]);
  const handleDebouncedCloneSearchChange = useCallback((value: string) => {
    setDebouncedCloneSearch(value);
  }, []);
  const cloneExecutionAvailableTags = useMemo(() => {
    const tags = new Set<string>();
    cloneVisibleExecutionRows.forEach((row) => {
      resolveEntityTags(row).forEach((tag) => {
        tags.add(tag);
      });
    });
    return Array.from(tags.values()).sort((left, right) => left.localeCompare(right));
  }, [cloneVisibleExecutionRows]);
  const handleDebouncedCloneExecutionSearchChange = useCallback((value: string) => {
    setDebouncedCloneExecutionSearch(value);
  }, []);
  const showCloneDeltas = viewModel.deltasEnabled;
  const cloneDateWindow = useCloneDateWindow({
    dateQuickFilter: cloneDateQuickFilter,
    fromDate: cloneFromDate,
    toDate: cloneToDate,
  });
  const cloneQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<SuiteListItemDto>> => {
      const toolbarFilters = mergeFilters(
        buildInFilter('latestExecutionStatus', cloneFilterStatuses),
        buildInFilter('tags', cloneFilterTags),
      );
      const sortOverride: ApiSort = {
        field: cloneSortFieldMap[cloneSortKey],
        direction: cloneSortDirection,
      };
      return querySuites(
        viewModel.selectedProjectId,
        viewModel.selectedOrganisationId ?? undefined,
        {
          ...input,
          sorts: [sortOverride],
          filters: [...input.filters, ...toolbarFilters],
          metricsStartDay: cloneDateWindow.startDay ?? undefined,
          metricsEndDay: cloneDateWindow.endDay ?? undefined,
          previousMetricsStartDay: cloneDateWindow.previousStartDay ?? undefined,
          previousMetricsEndDay: cloneDateWindow.previousEndDay ?? undefined,
          include: {
            project: true,
            latestExecution: true,
            latestTestResults: { counts: true },
            metrics: true,
            metricDeltas: showCloneDeltas,
          },
        },
      );
    },
    [
      cloneDateWindow.endDay,
      cloneDateWindow.previousEndDay,
      cloneDateWindow.previousStartDay,
      cloneDateWindow.startDay,
      cloneFilterStatuses,
      cloneFilterTags,
      cloneSortDirection,
      cloneSortKey,
      showCloneDeltas,
      viewModel.selectedOrganisationId,
      viewModel.selectedProjectId,
    ],
  );
  const cloneExecutionQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<SuiteExecutionDto>> => {
      const isLatestQuickFilter =
        cloneDateQuickFilter === 'latest' && cloneFromDate === null && cloneToDate === null;
      const baseFilters = isLatestQuickFilter
        ? input.filters.filter(
            (filter) =>
              filter.field !== 'timestamp' &&
              filter.field !== 'createdAt' &&
              filter.field !== 'isLatest',
          )
        : input.filters;
      const toolbarFilters = mergeFilters(
        buildInFilter('status', cloneExecutionFilterStatuses),
        buildInFilter('tags', cloneExecutionFilterTags),
        isLatestQuickFilter
          ? buildEqFilterValue('isLatest', true)
          : buildDateRangeFilters(
              'timestamp',
              cloneDateWindow.startDay
                ? dayjs(cloneDateWindow.startDay).startOf('day').toISOString()
                : null,
              cloneDateWindow.endDay
                ? dayjs(cloneDateWindow.endDay).endOf('day').toISOString()
                : null,
            ),
      );
      const sortOverride: ApiSort = {
        field: cloneExecutionSortFieldMap[cloneExecutionSortKey],
        direction: cloneExecutionSortDirection,
      };
      return querySuiteExecutions(
        viewModel.selectedProjectId,
        viewModel.selectedOrganisationId ?? undefined,
        {
          ...input,
          search: debouncedCloneExecutionSearch,
          sorts: [sortOverride],
          filters: [...baseFilters, ...toolbarFilters],
        },
      );
    },
    [
      cloneDateWindow.endDay,
      cloneDateWindow.startDay,
      cloneDateQuickFilter,
      cloneFromDate,
      cloneToDate,
      cloneExecutionFilterStatuses,
      cloneExecutionFilterTags,
      cloneExecutionSortDirection,
      cloneExecutionSortKey,
      debouncedCloneExecutionSearch,
      viewModel.selectedOrganisationId,
      viewModel.selectedProjectId,
    ],
  );
  const cloneNoRowsText = cloneDateWindow.hasCustomBounds
    ? 'No suites found for selected date range'
    : formatRangeWindowNoDataText('No suites found', cloneDateQuickFilter, false);
  const cloneExecutionsNoRowsText = cloneDateWindow.hasCustomBounds
    ? 'No suite executions found for selected date range'
    : formatRangeWindowNoDataText('No suite executions found', cloneDateQuickFilter, false);
  const cloneStatusFilterKey = useMemo(
    () => [...cloneFilterStatuses].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneFilterStatuses],
  );
  const cloneTagFilterKey = useMemo(
    () => [...cloneFilterTags].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneFilterTags],
  );
  const cloneExecutionStatusFilterKey = useMemo(
    () =>
      [...cloneExecutionFilterStatuses].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneExecutionFilterStatuses],
  );
  const cloneExecutionTagFilterKey = useMemo(
    () => [...cloneExecutionFilterTags].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneExecutionFilterTags],
  );
  const cloneContentOverride = cloneTableEnabled ? (
    <Panel
      padding="md"
      gap="none"
      contentGap="md"
      headerGap="none"
      headerSlot={
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: `${CLONE_TABLE_PANEL_HEADER_HEIGHT}px`,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap="md"
            sx={{
              minWidth: 0,
              minHeight: `${CLONE_TABLE_PANEL_HEADER_HEIGHT}px`,
              height: `${CLONE_TABLE_PANEL_HEADER_HEIGHT}px`,
            }}
          >
            <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
              <Tabs
                value={cloneActiveTab}
                onChange={(_event, next) => {
                  const nextTab = next as CloneSuitesPanelTab;
                  setCloneActiveTab(nextTab);
                  if (nextTab === 'suites' && cloneDateQuickFilter === 'latest') {
                    setCloneDateQuickFilter('7d');
                  }
                }}
                aria-label="Clone suites panel tabs"
              >
                <Tab label="Suites" value="suites" />
                <Tab label="Executions" value="executions" />
              </Tabs>
            </Box>
            <Box sx={{ flex: '0 0 auto', display: cloneActiveTab === 'suites' ? 'block' : 'none' }}>
              <SuitesCloneToolbar
                title={undefined}
                onDebouncedCloneSearchChange={handleDebouncedCloneSearchChange}
                columnVisibilityModel={cloneColumnVisibilityModel}
                onColumnVisibilityModelChange={setCloneColumnVisibilityModel}
                sortKey={cloneSortKey}
                sortDirection={cloneSortDirection}
                onSortChange={(nextSortKey, nextSortDirection) => {
                  setCloneSortKey(nextSortKey);
                  setCloneSortDirection(nextSortDirection);
                }}
                filterStatuses={cloneFilterStatuses}
                onFilterStatusesChange={setCloneFilterStatuses}
                filterTags={cloneFilterTags}
                onFilterTagsChange={setCloneFilterTags}
                dateQuickFilter={cloneDateQuickFilter}
                onDateQuickFilterChange={(next) => {
                  setCloneDateQuickFilter(next);
                  setCloneFromDate(null);
                  setCloneToDate(null);
                }}
                fromDate={cloneFromDate}
                toDate={cloneToDate}
                onFromDateChange={setCloneFromDate}
                onToDateChange={setCloneToDate}
                availableTags={cloneAvailableTags}
              />
            </Box>
            <Box
              sx={{
                flex: '0 0 auto',
                display: cloneActiveTab === 'executions' ? 'block' : 'none',
              }}
            >
              <SuiteExecutionsCloneToolbar
                title={undefined}
                onDebouncedCloneSearchChange={handleDebouncedCloneExecutionSearchChange}
                columnVisibilityModel={cloneExecutionColumnVisibilityModel}
                onColumnVisibilityModelChange={setCloneExecutionColumnVisibilityModel}
                sortKey={cloneExecutionSortKey}
                sortDirection={cloneExecutionSortDirection}
                onSortChange={(nextSortKey, nextSortDirection) => {
                  setCloneExecutionSortKey(nextSortKey);
                  setCloneExecutionSortDirection(nextSortDirection);
                }}
                filterStatuses={cloneExecutionFilterStatuses}
                onFilterStatusesChange={setCloneExecutionFilterStatuses}
                filterTags={cloneExecutionFilterTags}
                onFilterTagsChange={setCloneExecutionFilterTags}
                dateQuickFilter={cloneDateQuickFilter}
                onDateQuickFilterChange={(next) => {
                  setCloneDateQuickFilter(next);
                  setCloneFromDate(null);
                  setCloneToDate(null);
                }}
                fromDate={cloneFromDate}
                toDate={cloneToDate}
                onFromDateChange={setCloneFromDate}
                onToDateChange={setCloneToDate}
                availableTags={cloneExecutionAvailableTags}
              />
            </Box>
          </Stack>
        </Box>
      }
      sx={{ backgroundColor: 'background.paper' }}
    >
      <Box sx={{ display: cloneActiveTab === 'suites' ? 'block' : 'none' }}>
        <SuitesTableClone
          persistKey={null}
          queryKey={[
            'suites-table-clone',
            cloneDateQuickFilter,
            viewModel.selectedOrganisationId ?? null,
            viewModel.selectedProjectId ?? null,
            showCloneDeltas ? 'deltas-on' : 'deltas-off',
            cloneSortKey,
            cloneSortDirection,
            cloneStatusFilterKey,
            cloneTagFilterKey,
            cloneDateWindow.startDay,
            cloneDateWindow.endDay,
          ]}
          queryFn={cloneQueryFn}
          pageSizeOptions={[5, 10, 25, 50]}
          initialPageSize={10}
          showDeltas={showCloneDeltas}
          noRowsText={cloneNoRowsText}
          showToolbar={false}
          disableColumnSorting
          disableColumnMenu
          search={debouncedCloneSearch}
          onRowsChange={(rows) => {
            setCloneVisibleRows(rows);
          }}
          sparklineRangeDays={cloneDateWindow.sparklineRangeDays}
          metricsStartDay={cloneDateWindow.startDay ?? undefined}
          metricsEndDay={cloneDateWindow.endDay ?? undefined}
          columnVisibilityModel={cloneColumnVisibilityModel}
          onColumnVisibilityModelChange={(nextModel) => {
            setCloneColumnVisibilityModel({
              ...nextModel,
              name: true,
            });
          }}
        />
      </Box>
      <Box sx={{ display: cloneActiveTab === 'executions' ? 'block' : 'none' }}>
        <SuiteExecutionsTableClone
          persistKey={null}
          queryKey={[
            'suite-executions-table-clone',
            cloneDateQuickFilter,
            viewModel.selectedOrganisationId ?? null,
            viewModel.selectedProjectId ?? null,
            cloneExecutionSortKey,
            cloneExecutionSortDirection,
            cloneExecutionStatusFilterKey,
            cloneExecutionTagFilterKey,
            debouncedCloneExecutionSearch,
            cloneDateWindow.startDay,
            cloneDateWindow.endDay,
          ]}
          queryFn={cloneExecutionQueryFn}
          pageSizeOptions={[5, 10, 25, 50]}
          initialPageSize={10}
          noRowsText={cloneExecutionsNoRowsText}
          showToolbar={false}
          disableColumnSorting
          disableColumnMenu
          search={debouncedCloneExecutionSearch}
          onRowsChange={(rows) => {
            setCloneVisibleExecutionRows(rows);
          }}
          columnVisibilityModel={cloneExecutionColumnVisibilityModel}
          onColumnVisibilityModelChange={(nextModel) => {
            setCloneExecutionColumnVisibilityModel({
              ...nextModel,
              name: true,
            });
          }}
        />
      </Box>
    </Panel>
  ) : undefined;

  return (
    <Stack gap="md">
      <EntityTrendsPageShell
        viewModel={viewModel}
        title="Suites"
        addButtonLabel="Add Suite"
        searchAriaLabel="Search suites"
        chartTabsAriaLabel="Suites chart tabs"
        pageTabsAriaLabel="Suites page tabs"
        entityTabLabel="Suites"
        chartEmptyStateTitle="No suite executions found"
        entityTableNoRowsText="No suites found"
        executionTableNoRowsText="No suite executions found"
        persistKey="suites-table"
        executionPersistKey="suite-executions-table"
        contentOverride={cloneContentOverride}
        renderEntityTable={({
          queryKey,
          queryFn,
          fixedFilters,
          showDeltas,
          persistKey,
          noRowsText,
        }) => (
          <SuitesTableClone
            persistKey={persistKey}
            queryKey={queryKey}
            queryFn={queryFn}
            pageSizeOptions={[10]}
            initialPageSize={10}
            disablePageSizeSelect
            showDeltas={showDeltas}
            fixedFilters={fixedFilters}
            noRowsText={noRowsText}
          />
        )}
        renderExecutionsTable={({ fixedFilters, persistKey, noRowsText }) => (
          <SuiteExecutionsTableClone
            persistKey={persistKey}
            fixedFilters={fixedFilters}
            pageSizeOptions={[10]}
            initialPageSize={10}
            disablePageSizeSelect
            noRowsText={noRowsText}
          />
        )}
      />
    </Stack>
  );
};
